import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const TOKEN_KEY = "mt_token";

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }
    return Promise.reject(error);
  }
);

export const api = {
  // auth
  login: (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),

  // users (admin)
  listUsers: () => client.get("/users").then((r) => r.data),
  createUser: (body) => client.post("/users", body).then((r) => r.data),
  updateUser: (id, body) => client.put(`/users/${id}`, body).then((r) => r.data),
  deleteUser: (id) => client.delete(`/users/${id}`).then((r) => r.data),

  // vertical-lead access controls (admin) + lead-only UX flags
  leadAccess: () => client.get("/config/lead-access").then((r) => r.data),
  saveLeadAccess: (body) => client.put("/config/lead-access", body).then((r) => r.data),
  leadsLogVisibility: () => client.get("/admin/leads/log-visibility").then((r) => r.data),
  leadsHideLogs: (vertical = null) => client.post("/admin/leads/hide-existing-logs", vertical ? { vertical } : {}).then((r) => r.data),
  leadsShowLogs: (vertical = null) => client.post("/admin/leads/show-existing-logs", vertical ? { vertical } : {}).then((r) => r.data),

  // lists
  lists: () => client.get("/lists").then((r) => r.data),
  updateLists: (body) => client.put("/lists", body).then((r) => r.data),
  seed: (force = false) => client.post(`/seed?force=${force}`).then((r) => r.data),

  // files (invoice uploads)
  uploadFile: (formData) => client.post("/files/upload", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  fileMeta: (id) => client.get(`/files/${id}/meta`).then((r) => r.data),
  fileUrl: (id) => `${API}/files/${id}?token=${encodeURIComponent(localStorage.getItem(TOKEN_KEY) || "")}`,
  // Force a browser download of an uploaded invoice file (blob + object URL so
  // the auth header is honoured and the file saves rather than opening inline).
  downloadFile: async (id, filename) => {
    const res = await client.get(`/files/${id}`, { params: { download: 1 }, responseType: "blob" });
    const dispo = res.headers["content-disposition"] || "";
    const m = dispo.match(/filename="?([^";]+)"?/);
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || (m && m[1]) || `invoice-${id}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // spends (invoice ledger)
  listSpends: (params = {}) => client.get("/spends", { params }).then((r) => r.data),
  reconciliation: () => client.get("/spends/reconciliation").then((r) => r.data),
  createSpend: (body) => client.post("/spends", body).then((r) => r.data),
  updateSpend: (id, body) => client.put(`/spends/${id}`, body).then((r) => r.data),
  toggleLock: (id, locked) => client.patch(`/spends/${id}/lock?locked=${locked}`).then((r) => r.data),
  overrideAgencyChannel: (id, agency_channel) => client.patch(`/spends/${id}/channel`, { agency_channel }).then((r) => r.data),
  deleteSpend: (id) => client.delete(`/spends/${id}`).then((r) => r.data),

  // metrics (funnel)
  metrics: () => client.get("/metrics").then((r) => r.data),
  updateMetrics: (entries) => client.put("/metrics", { entries }).then((r) => r.data),

  // sales ledger (per-unit revenue)
  listSales: (params = {}) => client.get("/sales", { params }).then((r) => r.data),
  createSale: (body) => client.post("/sales", body).then((r) => r.data),
  updateSale: (id, body) => client.put(`/sales/${id}`, body).then((r) => r.data),
  deleteSale: (id) => client.delete(`/sales/${id}`).then((r) => r.data),
  targets: () => client.get("/targets").then((r) => r.data),

  // reports
  overview: (period = "FY", projects = null) => client.get(`/reports/overview`, { params: { period, ...(projects && projects.length ? { projects: projects.join(",") } : {}) } }).then((r) => r.data),
  marketingHead: (period = "FY", projects = null, includeCorporate = false) => client.get(`/reports/marketing-head`, { params: { period, ...(projects && projects.length ? { projects: projects.join(",") } : {}), ...(includeCorporate ? { include_corporate: true } : {}) } }).then((r) => r.data),
  corporateOverview: (period = "FY", projects = null) => client.get(`/reports/corporate-overview`, { params: { period, ...(projects && projects.length ? { projects: projects.join(",") } : {}) } }).then((r) => r.data),
  corporateEntity: (entity, period = "FY") => client.get(`/reports/corporate-entity`, { params: { entity, period } }).then((r) => r.data),
  corporateDrill: (vertical, { entity = null, platform = null, period = "FY" } = {}) => client.get(`/reports/corporate-drill`, { params: { vertical, period, ...(entity ? { entity } : {}), ...(platform != null ? { platform } : {}) } }).then((r) => r.data),
  strategyNotes: () => client.get(`/strategy/notes`).then((r) => r.data),
  saveStrategyNotes: (body) => client.put(`/strategy/notes`, body).then((r) => r.data),
  customMonths: () => client.get(`/config/months`).then((r) => r.data),
  saveCustomMonths: (months) => client.put(`/config/months`, { months }).then((r) => r.data),
  planActivities: (params = {}) => client.get(`/marketing-plan/activities`, { params }).then((r) => r.data),
  createPlanActivity: (body) => client.post(`/marketing-plan/activities`, body).then((r) => r.data),
  updatePlanActivity: (id, body) => client.put(`/marketing-plan/activities/${id}`, body).then((r) => r.data),
  deletePlanActivity: (id) => client.delete(`/marketing-plan/activities/${id}`).then((r) => r.data),
  marketingOutcomes: () => client.get(`/marketing-outcomes`).then((r) => r.data),
  createOutcome: (body) => client.post(`/marketing-outcomes`, body).then((r) => r.data),
  updateOutcome: (id, body) => client.put(`/marketing-outcomes/${id}`, body).then((r) => r.data),
  deleteOutcome: (id) => client.delete(`/marketing-outcomes/${id}`).then((r) => r.data),
  uploadPlanActivities: (formData, params = {}) => client.post(`/marketing-plan/activities/upload`, formData, { params }).then((r) => r.data),
  uploadOutcomes: (formData, params = {}) => client.post(`/marketing-outcomes/upload`, formData, { params }).then((r) => r.data),

  // ---- Data Manager (distinct from the invoice-ledger /spends endpoints above) ----
  dataImport: (formData) => client.post(`/data/import`, formData).then((r) => r.data),
  dataImportPreview: (formData) => client.post(`/data/import/preview`, formData).then((r) => r.data),
  dataImportCommit: (token) => client.post(`/data/import/commit`, { token }).then((r) => r.data),
  dataImportSpends: (formData) => client.post(`/data/import-spends`, formData).then((r) => r.data),
  // vertical mapping + guided, non-destructive invoice append
  verticalMappings: () => client.get(`/config/vertical-mappings`).then((r) => r.data),
  saveVerticalMappings: (body) => client.put(`/config/vertical-mappings`, body).then((r) => r.data),
  analyzeInvoices: (formData) => client.post(`/data/invoices/analyze`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  appendInvoices: (formData) => client.post(`/data/invoices/append`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  dataImportBudgetAllocation: (formData) => client.post(`/data/import-budget-allocation`, formData).then((r) => r.data),
  dataImportInvoices: (formData) => client.post(`/data/import-invoices`, formData).then((r) => r.data),
  // PART 1 — spend taxonomy (verticals by type + corporate entities) admin CRUD
  getSpendTaxonomy: () => client.get(`/taxonomy`).then((r) => r.data),
  saveSpendTaxonomy: (body) => client.put(`/taxonomy`, body).then((r) => r.data),
  addVertical: (body) => client.post(`/taxonomy/verticals`, body).then((r) => r.data),
  removeVertical: (name, reassignTo = null) => client.delete(`/taxonomy/verticals/${encodeURIComponent(name)}`, { params: reassignTo ? { reassign_to: reassignTo } : {} }).then((r) => r.data),
  renameVertical: (name, newName, label = null) => client.patch(`/taxonomy/verticals/${encodeURIComponent(name)}`, { new_name: newName, ...(label ? { label } : {}) }).then((r) => r.data),
  // payment status — Excel template + bulk import (matched by row id)
  paymentStatusTemplate: (params = {}) => client.get(`/data/payment-status-template`, { params, responseType: "blob" }),
  importPaymentStatus: (formData) => client.post(`/data/import-payment-status`, formData).then((r) => r.data),
  dataImportFunnel: (formData) => client.post(`/data/import-funnel`, formData).then((r) => r.data),
  dataFunnelTemplate: () => client.get(`/data/funnel-template`, { responseType: "blob" }),
  tagAudit: (params = {}) => client.get(`/reports/tag-audit`, { params }).then((r) => r.data),
  dataSummary: () => client.get(`/data/summary`).then((r) => r.data),
  dataListSpends: (params = {}) => client.get(`/data/spends`, { params }).then((r) => r.data),
  dataSpendIds: (params = {}) => client.get(`/data/spend-ids`, { params }).then((r) => r.data),
  dataCreateSpend: (body) => client.post(`/data/spends`, body).then((r) => r.data),
  dataUpdateSpend: (id, body) => client.put(`/data/spends/${id}`, body).then((r) => r.data),
  dataDeleteSpend: (id) => client.delete(`/data/spends/${id}`).then((r) => r.data),
  dataBulkUpdateSpends: (body) => client.post(`/data/spends/bulk-update`, body).then((r) => r.data),
  dataBulkDeleteSpends: (body) => client.post(`/data/spends/bulk-delete`, body).then((r) => r.data),
  dataPurgeAllSpends: () => client.delete(`/data/spends`).then((r) => r.data),
  dataVendors: () => client.get(`/data/vendors`).then((r) => r.data),
  dataVendorSuggestions: () => client.get(`/data/vendors/suggestions`).then((r) => r.data),
  dataMergeVendors: (body) => client.post(`/data/vendors/merge`, body).then((r) => r.data),
  dataExport: () => client.get(`/data/export`, { responseType: "blob" }),
  dataListFunnel: (params = {}) => client.get(`/data/funnel`, { params }).then((r) => r.data),
  dataCreateFunnel: (body) => client.post(`/data/funnel`, body).then((r) => r.data),
  dataUpdateFunnel: (id, body) => client.put(`/data/funnel/${id}`, body).then((r) => r.data),
  dataDeleteFunnel: (id) => client.delete(`/data/funnel/${id}`).then((r) => r.data),
  curatedConfig: () => client.get(`/config/curated`).then((r) => r.data),
  addCuratedProject: (body) => client.post(`/config/curated/projects`, body).then((r) => r.data),
  deleteCuratedProject: (name) => client.delete(`/config/curated/projects/${encodeURIComponent(name)}`).then((r) => r.data),
  addCuratedVertical: (body) => client.post(`/config/curated/verticals`, body).then((r) => r.data),
  deleteCuratedVertical: (name) => client.delete(`/config/curated/verticals/${encodeURIComponent(name)}`).then((r) => r.data),
  plannedBudgets: () => client.get(`/planned-budgets`).then((r) => r.data),
  savePlannedBudget: (body) => client.put(`/planned-budgets`, body).then((r) => r.data),
  analytics: (params = {}) => client.get(`/reports/analytics`, { params }).then((r) => r.data),
  // PART 9 · AC4 — Data-integrity parity sweep (admin only)
  parityCheck: (period = "FY") => client.get(`/reports/parity-check`, { params: { period } }).then((r) => r.data),
  budgetPlanner: (fy = "FY2026-27") => client.get(`/reports/budget-allocation`, { params: { fy } }).then((r) => r.data),
  saveBudgetPlanner: (fy, rows) => client.put(`/reports/budget-allocation`, { fy, rows }).then((r) => r.data),
  monthly: (month) => client.get(`/reports/monthly?month=${encodeURIComponent(month)}`).then((r) => r.data),
  projects: (period = "FY") => client.get(`/reports/projects?period=${encodeURIComponent(period)}`).then((r) => r.data),
  projectDetail: (name, period = "FY") => client.get(`/reports/project/${encodeURIComponent(name)}`, { params: { period } }).then((r) => r.data),
  channels: (period = "FY") => client.get(`/reports/channels?period=${encodeURIComponent(period)}`).then((r) => r.data),
  platforms: (period = "FY") => client.get(`/reports/platforms?period=${encodeURIComponent(period)}`).then((r) => r.data),
  verticals: (params = {}) => client.get(`/reports/verticals`, { params }).then((r) => r.data),
  spendsByVertical: (params = {}) => client.get(`/reports/spends-by-vertical`, { params }).then((r) => r.data),
  spendSegmentation: (params = {}) => client.get(`/reports/spend-segmentation`, { params }).then((r) => r.data),
  spendsOverview: (period = "FY") => client.get(`/reports/spends-overview`, { params: { period } }).then((r) => r.data),
  fiscalYears: () => client.get(`/reports/fiscal-years`).then((r) => r.data),
  saveFiscalYears: (years, def) => client.put(`/reports/fiscal-years`, { years, default: def }).then((r) => r.data),
  spendCategoryValues: (field) => client.get(`/spends/category-values`, { params: { field } }).then((r) => r.data),
  recategorizeSpends: (field, from_values, to_value) => client.post(`/spends/recategorize`, { field, from_values, to_value }).then((r) => r.data),
  investmentMix: (params = {}) => client.get(`/reports/investment-mix`, { params }).then((r) => r.data),
  investmentMixLines: (params = {}) => client.get(`/reports/investment-mix/lines`, { params }).then((r) => r.data),

  // Marketing Plan: Targets & Outcomes (Data-tab driven)
  planOutcomes: (params = {}) => client.get(`/plan/outcomes`, { params }).then((r) => r.data),
  patchPlanOutcome: (id, field, value) => client.patch(`/plan/outcomes/${id}`, { field, value }).then((r) => r.data),
  uploadPlanOutcomes: (formData) => client.post(`/plan/outcomes/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  reseedPlanOutcomes: () => client.post(`/plan/outcomes/reseed`).then((r) => r.data),
  spendExplorer: (params = {}) => client.get(`/reports/spend-explorer`, { params }).then((r) => r.data),
  spendDrill: (params = {}) => client.get(`/reports/spend-drill`, { params }).then((r) => r.data),
  spendList: (params = {}) => client.get(`/reports/spend-list`, { params }).then((r) => r.data),

  // Phase 6-9: vertical targets, conversion benchmarks, digest, alerts, exports
  verticalTargets: (params = {}) => client.get(`/targets/vertical`, { params }).then((r) => r.data),
  uploadVerticalTargets: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/targets/vertical/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  conversionBenchmarks: () => client.get(`/targets/conversion-benchmarks`).then((r) => r.data),
  saveConversionBenchmark: (body) => client.put(`/targets/conversion-benchmarks`, body).then((r) => r.data),
  ceoDigest: () => client.get(`/reports/ceo-digest`).then((r) => r.data),
  budgetAlerts: () => client.get(`/reports/budget-alerts`).then((r) => r.data),
  // CEO Expansion Pack: vendor scorecards, quarterly rollups, alert thresholds
  vendorScorecards: (period = "FY") => client.get(`/reports/vendor-scorecards`, { params: { period } }).then((r) => r.data),
  referralTracker: (period = "FY", projects = null) => client.get(`/reports/referral-tracker`, { params: { period, ...(projects && projects.length ? { projects: projects.join(",") } : {}) } }).then((r) => r.data),
  alertThresholds: () => client.get(`/config/alert-thresholds`).then((r) => r.data),
  saveAlertThresholds: (body) => client.put(`/config/alert-thresholds`, body).then((r) => r.data),
  downloadExport: async (path, params = {}, fallbackName = "export.xlsx") => {
    const res = await client.get(path, { params, responseType: "blob" });
    const dispo = res.headers["content-disposition"] || "";
    const m = dispo.match(/filename="?([^";]+)"?/);
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = (m && m[1]) || fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  spendSegmentationExport: (params = {}) => client.get(`/reports/spend-segmentation/export`, { params, responseType: "blob" }),
  taxonomy: (params = {}) => client.get(`/reports/taxonomy`, { params }).then((r) => r.data),
  // benchmark governed edit
  editBenchmark: (body) => client.put(`/targets/benchmark`, body).then((r) => r.data),
  // real budget plan (Phase H3)
  budgetPlan: (params = {}) => client.get(`/budget-plan`, { params }).then((r) => r.data),
  saveBudgetPlan: (body) => client.put(`/budget-plan`, body).then((r) => r.data),
  efficiency: () => client.get(`/budget-plan/efficiency`).then((r) => r.data),
  editEfficiency: (body) => client.put(`/budget-plan/efficiency`, body).then((r) => r.data),
  // channel-level budget plan (Phase I)
  planTaxonomy: () => client.get(`/plan/taxonomy`).then((r) => r.data),
  planAddChannel: (body) => client.post(`/plan/channels`, body).then((r) => r.data),
  planRenameChannel: (id, body) => client.put(`/plan/channels/${id}`, body).then((r) => r.data),
  planDeleteChannel: (id) => client.delete(`/plan/channels/${id}`).then((r) => r.data),
  planAllocations: (params = {}) => client.get(`/plan/allocations`, { params }).then((r) => r.data),
  savePlanAllocations: (body) => client.put(`/plan/allocations`, body).then((r) => r.data),
  planTargets: () => client.get(`/plan/targets`).then((r) => r.data),
  editVerticalPct: (body) => client.put(`/plan/vertical-pct`, body).then((r) => r.data),
  editVerticalTarget: (body) => client.put(`/plan/vertical-target`, body).then((r) => r.data),
  // editable dashboard numbers + config (Phase J)
  editDashboardValue: (body) => client.put(`/dashboard/value`, body).then((r) => r.data),
  getKpiOverrides: (period = "FY", projects = null) => client.get(`/dashboard/kpis/override`, { params: { period, ...(projects && projects.length ? { projects: projects.join(",") } : {}) } }).then((r) => r.data),
  overrideDashboardKpi: (body) => client.patch(`/dashboard/kpis/override`, body).then((r) => r.data),
  sumadhuraBrand: (period = "FY") => client.get(`/reports/sumadhura-brand`, { params: { period } }).then((r) => r.data),
  dashboardConfig: () => client.get(`/config/dashboard`).then((r) => r.data),
  saveDashboardConfig: (body) => client.put(`/config/dashboard`, body).then((r) => r.data),
  targetsModule: (params = {}) => client.get(`/reports/targets-module`, { params }).then((r) => r.data),
  reiteration: (params = {}) => client.get(`/reports/reiteration`, { params }).then((r) => r.data),
  // Targets v2 — project-first overview + budget vs allocation + SV:Booking ratio
  targetsOverview: (params = {}) => client.get(`/reports/targets-overview`, { params }).then((r) => r.data),
  budgetVsAllocation: (params = {}) => client.get(`/reports/budget-vs-allocation`, { params }).then((r) => r.data),
  saveBudgetMatrix: (entries) => client.put(`/reports/budget-vs-allocation`, { entries }).then((r) => r.data),
  // Monthly Budget Engine (Round 2) — per project × month mgmt + planned budgets + vertical weights
  budgetMonthly: (params = {}) => client.get(`/reports/budget-monthly`, { params }).then((r) => r.data),
  saveBudgetMonthly: (entries) => client.put(`/reports/budget-monthly`, { entries }).then((r) => r.data),
  saveBudgetWeights: (entries) => client.put(`/reports/budget-vertical-weights`, { entries }).then((r) => r.data),
  svBookingRatio: () => client.get(`/targets/sv-booking-ratio`).then((r) => r.data),
  saveSvBookingRatio: (body) => client.put(`/targets/sv-booking-ratio`, body).then((r) => r.data),
  targetsProject: (project) => client.get(`/reports/targets-project`, { params: { project } }).then((r) => r.data),
  projectVerticalTargets: (project, kind = "marketing") => client.get(`/targets/project-vertical`, { params: { project, kind } }).then((r) => r.data),
  saveVerticalTargets: (body) => client.put(`/targets/vertical-batch`, body).then((r) => r.data),
  editMarketing: (body) => client.put(`/targets/marketing`, body).then((r) => r.data),
  resetMarketing: (body = {}) => client.post(`/targets/marketing/reset`, body).then((r) => r.data),
  commitReiteration: (body = {}) => client.post(`/targets/reiteration/commit`, body).then((r) => r.data),
  editTargetsKpi: (body) => client.put(`/targets/kpi`, body).then((r) => r.data),
  // budget simulator
  budgetBase: () => client.get(`/budget/base`).then((r) => r.data),
  budgetScenarios: () => client.get(`/budget/scenarios`).then((r) => r.data),
  createScenario: (body) => client.post(`/budget/scenarios`, body).then((r) => r.data),
  updateScenario: (id, body) => client.put(`/budget/scenarios/${id}`, body).then((r) => r.data),
  deleteScenario: (id) => client.delete(`/budget/scenarios/${id}`).then((r) => r.data),
  commitScenario: (id) => client.post(`/budget/scenarios/${id}/commit`).then((r) => r.data),
  campaignsReport: (period = "FY") => client.get(`/reports/campaigns?period=${encodeURIComponent(period)}`).then((r) => r.data),
  campaignDetail: (id, period = "FY") => client.get(`/reports/campaign/${encodeURIComponent(id)}?period=${encodeURIComponent(period)}`).then((r) => r.data),

  // vertical budgets (project x month x vertical)
  verticalBudgets: (params = {}) => client.get(`/vertical-budgets`, { params }).then((r) => r.data),
  saveVerticalBudgets: (entries) => client.put(`/vertical-budgets`, { entries }).then((r) => r.data),

  // campaigns
  listCampaigns: () => client.get(`/campaigns`).then((r) => r.data),
  createCampaign: (body) => client.post(`/campaigns`, body).then((r) => r.data),
  updateCampaign: (id, body) => client.put(`/campaigns/${id}`, body).then((r) => r.data),
  deleteCampaign: (id) => client.delete(`/campaigns/${id}`).then((r) => r.data),

  // campaign channel lines + dated performance updates
  listChannels: (cid) => client.get(`/campaigns/${cid}/channels`).then((r) => r.data),
  createChannel: (cid, body) => client.post(`/campaigns/${cid}/channels`, body).then((r) => r.data),
  updateChannel: (lid, body) => client.put(`/campaigns/channels/${lid}`, body).then((r) => r.data),
  deleteChannel: (lid) => client.delete(`/campaigns/channels/${lid}`).then((r) => r.data),
  listPerf: (lid) => client.get(`/campaigns/channels/${lid}/perf`).then((r) => r.data),
  createPerf: (lid, body) => client.post(`/campaigns/channels/${lid}/perf`, body).then((r) => r.data),
  deletePerf: (pid) => client.delete(`/campaigns/perf/${pid}`).then((r) => r.data),
  campaignPerformance: (params = {}) => client.get(`/reports/campaign-performance`, { params }).then((r) => r.data),

  // invoices & payments (finance)
  invoicesReport: (params = {}) => client.get(`/reports/invoices`, { params }).then((r) => r.data),
  updatePayment: (id, body) => client.patch(`/spends/${id}/payment`, body).then((r) => r.data),

  // invoice approval workflow
  approveStage: (id, body) => client.post(`/spends/${id}/approve`, body).then((r) => r.data),
  financeSubmit: (id, body) => client.post(`/spends/${id}/finance-submit`, body).then((r) => r.data),

  // budget provided (top-down)
  budgetProvided: (params = {}) => client.get(`/budget-provided`, { params }).then((r) => r.data),
  saveBudgetProvided: (entries) => client.put(`/budget-provided`, { entries }).then((r) => r.data),

  // budget allocated (annual per-project cap, management → marketing head)
  budgetAllocated: (params = {}) => client.get(`/budget-allocated`, { params }).then((r) => r.data),
  saveBudgetAllocated: (entries) => client.put(`/budget-allocated`, { entries }).then((r) => r.data),

  // targets grid (project x vertical x month)
  targetsGrid: (params = {}) => client.get(`/targets-grid`, { params }).then((r) => r.data),
  saveTargets: (entries) => client.put(`/targets-grid`, { entries }).then((r) => r.data),
  targetsReport: (params = {}) => client.get(`/reports/targets`, { params }).then((r) => r.data),

  // audit log
  audit: (params = {}) => client.get(`/audit`, { params }).then((r) => r.data),

  // PART 8 — Admin CMS: editable content (titles / labels / descriptions)
  cmsContent: () => client.get(`/cms/content`).then((r) => r.data),
  cmsRegistry: () => client.get(`/cms/registry`).then((r) => r.data),
  saveCmsContent: (body) => client.put(`/cms/content`, body).then((r) => r.data),
  resetCmsContent: (key) => client.delete(`/cms/content/${encodeURIComponent(key)}`).then((r) => r.data),
};

export default client;
